import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import LineExternalLinkAltIcon from 'LuxKit/Icons/line/LineExternalLinkAltIcon'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import Heading from 'LuxKit/Typography/Heading'
import { useIntl } from 'react-intl'
import type { DrawerHotelPrice } from './types'

interface Props {
  price?: DrawerHotelPrice
  value?: number
  discountPercent?: number
  offerId: string
  deeplinkUrl?: string
  saleUnit?: string
  isBooked?: boolean
  orderId?: string
}

function HotelDetailPricing({
  price,
  value,
  discountPercent,
  offerId,
  deeplinkUrl,
  saleUnit,
  isBooked,
  orderId,
}: Props) {
  const intl = useIntl()
  const bookNowHref = deeplinkUrl || `/offer/${offerId}`

  const bookNowLabel = intl.formatMessage({
    id: 'jPThW3ju3Ar48FaMK2Va',
    defaultMessage: 'Book now',
    description: 'Call-to-action button text to book the selected hotel offer on the offer page',
  })

  const bookingDetailsLabel = intl.formatMessage({
    id: 'V3if7yCh9Y75j9TcYdbF',
    defaultMessage: 'Booking details',
    description: 'CTA button text to view booking details in trip planner',
  })

  if (isBooked) {
    return (
      <Group direction="horizontal" horizontalAlign="end">
        <TextButton
          kind="tertiary"
          size="large"
          href={orderId ? `/account/my-escapes/order/${orderId}` : '/account/my-escapes'}
          target="_blank"
          rel="noopener noreferrer"
        >
          {bookingDetailsLabel}
        </TextButton>
      </Group>
    )
  }

  if (!price) {
    return (
      <Group direction="vertical" gap={16}>
        <Heading variant="heading4">
          {intl.formatMessage({
            id: 'haM13lTwzMAV8P10PNiJ',
            defaultMessage: 'Pricing',
            description: 'Heading for hotel pricing section on the hotel or room booking page',
          })}
        </Heading>
        <Group
          direction="vertical"
          tabletDirection="horizontal"
          gap={16}
          tabletHorizontalAlign="space-between"
          tabletVerticalAlign="center"
        >
          <TextLink
            size="medium"
            href={bookNowHref}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<LineExternalLinkAltIcon />}
          >
            {intl.formatMessage({
              id: 'wBc1bOKcrj5AocujDj03',
              defaultMessage: 'View on Luxury Escapes for pricing details',
              description: 'Link to view hotel pricing on Luxury Escapes website',
            })}
          </TextLink>
          <TextButton
            kind="primary"
            size="large"
            endIcon={<LineExternalLinkAltIcon />}
            href={bookNowHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            {bookNowLabel}
          </TextButton>
        </Group>
      </Group>
    )
  }

  const priceLabel = intl.formatMessage(
    {
      id: 'TmpthJ75SbMB0p3uodV5',
      defaultMessage: '{count, plural, one {# night} other {# nights}} from',
      description: 'Hotel pricing caption showing number of nights in the trip planner AI chat',
    },
    { count: price.nights },
  )
  const hasValue = value !== undefined && value > 0 && value >= price.total

  return (
    <Group
      direction="vertical"
      tabletDirection="horizontal"
      gap={16}
      tabletHorizontalAlign="space-between"
      tabletVerticalAlign="end"
    >
      <Group direction="vertical" gap={4}>
        <PriceRowPriceCaption>{priceLabel}</PriceRowPriceCaption>
        <PriceRowPrice
          size="L"
          price={price.total}
          saleUnit={saleUnit ?? 'total'}
        />
        {hasValue && (
          <PriceRowValueDiscountWithCaption
            size="M"
            originalValue={value}
            discountPercentage={discountPercent}
          />
        )}
      </Group>
      <TextButton
        kind="primary"
        size="large"
        endIcon={<LineExternalLinkAltIcon />}
        href={bookNowHref}
        target="_blank"
        rel="noopener noreferrer"
      >
        {bookNowLabel}
      </TextButton>
    </Group>
  )
}

export default HotelDetailPricing
